/**
 * First-party reporting for the /get-app download page.
 *
 * GA4 alone cannot measure this page: its script loads afterInteractive while
 * the redirect fires from an effect, so on a phone the pageview races the
 * navigation and often loses. sendBeacon is built for exactly this — the
 * browser guarantees a delivery attempt after unload.
 */
import { BRAND } from './brand';

const ATLAS_BEACON_URL =
  'https://app.ship-planner.com.au/api/inventory/insights/app-get-click';

export type AppGetDestination = 'app-store' | 'play-store' | 'lander';

/** iPadOS 13+ reports a Mac UA; touch points are the reliable tell. */
export function detectDevice(
  ua: string,
  platform: string,
  maxTouchPoints: number,
): 'ios' | 'android' | 'desktop' {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (platform === 'MacIntel' && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export function reportAppGetClick(
  destination: AppGetDestination,
  source: string,
): void {
  if (typeof window === 'undefined') return;

  const device = detectDevice(
    navigator.userAgent || '',
    navigator.platform || '',
    navigator.maxTouchPoints || 0,
  );

  // GA4 too, so whoever looks there sees the same story. Best effort.
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.('event', 'app_store_redirect', { destination, source, device });

  const body = JSON.stringify({
    brand: BRAND.key,
    device,
    destination,
    source,
    referrer: document.referrer || '',
    userAgent: navigator.userAgent || '',
  });

  try {
    // MUST be text/plain. An application/json Blob makes this a non-simple
    // request, so the browser tries a CORS preflight — which sendBeacon
    // cannot perform — and drops the beacon without a console error.
    navigator.sendBeacon(
      ATLAS_BEACON_URL,
      new Blob([body], { type: 'text/plain' }),
    );
  } catch {
    // Reporting must never break the redirect.
  }
}
